import api from './api';
import { getCarrierClass } from './carrierUtils';

export interface WarehouseDetails {
  id: number;
  name: string;
  ship_from_name: string;
  ship_from_company: string;
  ship_from_country: string;
  ship_from_address1: string;
  ship_from_address2: string;
  ship_from_zipcode: string;
  ship_from_city: string;
  ship_from_state: string;
  ship_from_phone: string;
  ship_from_email: string;
  ship_ref_append: string;
}

export interface ShippingDetails {
  name: string;
  company: string;
  country: string;
  address1: string;
  address2: string;
  zipCode: string;
  city: string;
  state: string;
  phone: string;
  email: string;
}

export interface Package {
  length: number;
  width: number;
  height: number;
  weight: number;
  numberOfPackages: number;
  commodity: {
    description: string;
    hsCode: string;
    manufactureCountry: string;
    value: number;
    quantity: number;
    productName?: string;
    hsVerified?: boolean;
    providedHsCode?: string;
    providedDescription?: string;
  };
}

export interface Rate {
  carrier: string;
  serviceName: string;
  totalCharge: number;
  estimatedDelivery: string;
  serviceCode: string;
  rateId: string;
  carrier_name?: string;
  base_charge?: number;
  surcharges?: any[];
  taxes?: any[];
  currency?: string;
  carrierClass?: string;
}

export const buildDetails = (
  shippingDetails: ShippingDetails,
  packages: Package[],
  warehouseDetails: WarehouseDetails,
  referenceNumber: string,
  requestGuaranteedRates: boolean = true
) => {
  const nextDay = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const year = nextDay.getFullYear();
  const month = nextDay.getMonth() + 1;
  const day = nextDay.getDate();

  const isDomestic = warehouseDetails.ship_from_country === shippingDetails.country;

  const origin = {
    name: warehouseDetails.ship_from_company || warehouseDetails.ship_from_name,
    address: {
      address_line_1: warehouseDetails.ship_from_address1,
      address_line_2: warehouseDetails.ship_from_address2 || '',
      unit_number: '',
      city: warehouseDetails.ship_from_city,
      region: warehouseDetails.ship_from_state,
      postal_code: warehouseDetails.ship_from_zipcode,
      country: warehouseDetails.ship_from_country,
    },
    residential: true,
    tailgate_required: false,
    instructions: '',
    contact_name: warehouseDetails.ship_from_name,
    phone_number: { number: warehouseDetails.ship_from_phone, extension: '' },
    email_addresses: [warehouseDetails.ship_from_email],
    receives_email_updates: true,
  };

  const destination = {
    name: shippingDetails.company || shippingDetails.name,
    address: {
      address_line_1: shippingDetails.address1,
      address_line_2: shippingDetails.address2 || '',
      unit_number: '',
      city: shippingDetails.city,
      region: shippingDetails.state,
      postal_code: shippingDetails.zipCode,
      country: shippingDetails.country,
    },
    residential: false,
    tailgate_required: false,
    instructions: '',
    contact_name: shippingDetails.name,
    phone_number: { number: shippingDetails.phone, extension: '' },
    email_addresses: [shippingDetails.email],
    receives_email_updates: true,
    ready_at: { hour: 8, minute: 0 },
    ready_until: { hour: 17, minute: 0 },
    signature_requirement: 'not-required',
  };

  const packaging_properties = {
    packages: packages.flatMap(pkg =>
      Array.from({ length: pkg.numberOfPackages }, () => ({
        measurements: {
          weight: { unit: 'lb', value: pkg.weight },
          cuboid: { unit: 'in', l: pkg.length, w: pkg.width, h: pkg.height },
        },
        description: pkg.commodity.description,
        special_handling_required: false,
      }))
    ),
    includes_return_label: false,
    has_dangerous_goods: false,
  };

  let customs_data;
  if (!isDomestic) {
    customs_data = {
      products: packages.map(pkg => {
        const verified = !!pkg.commodity.hsVerified;
        const product: any = {
          product_name: verified ? (pkg.commodity.providedDescription || pkg.commodity.description || 'Unknown Item') : (pkg.commodity.productName || pkg.commodity.description || 'Unknown Item'),
          hs_code: pkg.commodity.hsCode?.trim(),
          country_of_origin: pkg.commodity.manufactureCountry || 'CA',
          num_units: pkg.commodity.quantity,
          unit_price: { currency: 'CAD', value: Math.round(pkg.commodity.value * 100).toString() },
          description: pkg.commodity.description || 'Unknown Item',
          cusma_included: true,
          non_auto_parts: false,
          fda_regulated: 'no',
          product_composition_allocation_zero: true,
          hsverified: verified,
          provided_hscode: verified ? (pkg.commodity.providedHsCode || pkg.commodity.hsCode?.trim()) : pkg.commodity.hsCode?.trim(),
          provided_description: verified ? (pkg.commodity.providedDescription || pkg.commodity.description) : pkg.commodity.description,
        };
        return product;
      }),
      request_guaranteed_customs_charges: requestGuaranteedRates,
    };
  }

  return {
    origin,
    destination,
    expected_ship_date: { year, month, day },
    packaging_type: 'package',
    packaging_properties,
    reference_codes: [referenceNumber],
    customs_data,
  };
};

export const buildPickupDetails = (warehouseDetails: WarehouseDetails) => {
  const now = new Date();
  const easternTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const isAfterNoon = easternTime.getHours() >= 12;
  let pickupDate = isAfterNoon ? new Date(easternTime.getTime() + 24 * 60 * 60 * 1000) : easternTime;
  const pickupHour = isAfterNoon ? 11 : 12;

  const year = pickupDate.getFullYear();
  const month = pickupDate.getMonth() + 1;
  const day = pickupDate.getDate();

  return {
    date: { year, month, day },
    ready_at: { hour: pickupHour, minute: 0 },
    ready_until: { hour: 16, minute: 30 },
    pickup_location: warehouseDetails.name,
    contact_name: 'Warehouse Contact',
    contact_phone_number: { number: warehouseDetails.ship_from_phone || '123-456-7890' },
  };
};

export const buildCustomsInvoice = (
  shippingDetails: ShippingDetails,
  packages: Package[],
  warehouseDetails: WarehouseDetails
) => {
  const isDomestic = warehouseDetails.ship_from_country === shippingDetails.country;

  if (isDomestic) {
    return undefined;
  }

  return {
    source: 'details',
    broker: {
      use_carrier: true,
    },
    details: {
      tax_recipient: {
        type: 'shipper',
        name: warehouseDetails.ship_from_name,
        address: {
          address_line_1: warehouseDetails.ship_from_address1,
          address_line_2: warehouseDetails.ship_from_address2 || '',
          unit_number: '',
          city: warehouseDetails.ship_from_city,
          region: warehouseDetails.ship_from_state,
          postal_code: warehouseDetails.ship_from_zipcode,
          country: warehouseDetails.ship_from_country,
        },
        phone_number: { number: warehouseDetails.ship_from_phone },
        reason_for_export: 'commercially-sold-goods',
      },
      products: packages.map(pkg => {
        const unitWeight = (pkg.numberOfPackages * pkg.weight) / pkg.commodity.quantity;
        const verified = !!pkg.commodity.hsVerified;
        const product: any = {
          product_name: verified ? (pkg.commodity.providedDescription || pkg.commodity.description || 'Unknown Item') : (pkg.commodity.productName || pkg.commodity.description || 'Unknown Item'),
          weight: { value: unitWeight, unit: 'lb' },
          hs_code: pkg.commodity.hsCode?.trim(),
          country_of_origin: pkg.commodity.manufactureCountry || 'CA',
          num_units: pkg.commodity.quantity,
          unit_price: { currency: 'CAD', value: Math.round(pkg.commodity.value * 100).toString() },
          description: pkg.commodity.description || 'Unknown Item',
          cusma_included: true,
          non_auto_parts: false,
          fda_regulated: 'no',
          product_composition_allocation_zero: true,
          hsverified: verified,
          provided_hscode: verified ? (pkg.commodity.providedHsCode || pkg.commodity.hsCode?.trim()) : pkg.commodity.hsCode?.trim(),
          provided_description: verified ? (pkg.commodity.providedDescription || pkg.commodity.description) : pkg.commodity.description,
        };
        return product;
      }),
    },
  };
};

export const checkDuplicateShipmentReference = async (shipmentReference: string): Promise<boolean> => {
  const response = await api.get(`/freightcom/freightcom?action=check-duplicate&shipment_reference=${encodeURIComponent(shipmentReference)}`);
  return response.data.exists;
};

export const validateHTS = async (
  shippingDetails: ShippingDetails,
  packages: Package[],
  warehouseDetails: WarehouseDetails,
  referenceNumber: string,
  requestGuaranteedRates: boolean = true
): Promise<any> => {
  const details = buildDetails(shippingDetails, packages, warehouseDetails, referenceNumber, requestGuaranteedRates);
  const payload = {
    details,
  };

  console.log('HTS Validate payload:', JSON.stringify(payload, null, 2));
  const response = await api.post('/freightcom/freightcom?action=hts-validate', payload);

  if (response.data.status === false) {
    throw new Error('Freightcom HTS validate API request failed', { cause: { response: { data: response.data } } });
  }

  return response.data.recommendations;
};

export const getRates = async (
  shippingDetails: ShippingDetails,
  packages: Package[],
  warehouseDetails: WarehouseDetails,
  referenceNumber: string,
  requestGuaranteedRates: boolean = true
): Promise<Rate[]> => {
  const details = buildDetails(shippingDetails, packages, warehouseDetails, referenceNumber, requestGuaranteedRates);
  const payload = {
    excluded_services: [],
    details,
  };

  console.log('Get Rates payload:', JSON.stringify(payload, null, 2));
  const response = await api.post('/freightcom/freightcom?action=rates', payload);

  if (response.data.status === false) {
    throw new Error('Freightcom API request failed', { cause: { response: { data: response.data } } });
  }

  const ratesData = response.data.rates.map((rate: any) => ({
    carrier: rate.carrier_name || 'Freightcom',
    serviceName: rate.service_name,
    totalCharge: rate.total / 100, // Convert from cents to dollars
    estimatedDelivery: rate.transit_days ? `${rate.transit_days} days` : 'N/A',
    serviceCode: rate.service_id,
    rateId: rate.service_id,
    carrier_name: rate.carrier_name,
    base_charge: rate.base_charge,
    surcharges: rate.surcharges,
    taxes: rate.taxes,
    currency: rate.currency,
    carrierClass: getCarrierClass(rate.carrier_name),
  }));

  // Sort rates by price (cheapest to most expensive)
  ratesData.sort((a: Rate, b: Rate) => a.totalCharge - b.totalCharge);

  return ratesData;
};

export const getLabel = async (
  selectedRate: Rate,
  shippingDetails: ShippingDetails,
  packages: Package[],
  warehouseDetails: WarehouseDetails,
  shipmentReference: string,
  orderId: number,
  warehouseId: number,
  requestGuaranteedRates: boolean = true
): Promise<{ labelUrl: string; trackingNumber: string; carrier: string }> => {
  const details = buildDetails(shippingDetails, packages, warehouseDetails, shipmentReference, requestGuaranteedRates);
  const customs_invoice = buildCustomsInvoice(shippingDetails, packages, warehouseDetails);
  const pickup_details = buildPickupDetails(warehouseDetails);

  // Fetch cusma document from settings if customs invoice exists
  let paperless_customs_documents: any[] = [];
  if (customs_invoice) {
    try {
      const settingsResponse = await api.get('/settings');
      const { cusma_document, cusma_file_name } = settingsResponse.data;
      paperless_customs_documents = [{
        type: 'cusma-form',
        file_name: cusma_file_name || 'cusma.pdf',
        file_base64: cusma_document || '',
      }];
    } catch (error) {
      console.error('Failed to fetch cusma document from settings:', error);
      // Fallback to empty document if fetch fails
      paperless_customs_documents = [{
        type: 'cusma-form',
        file_name: 'cusma.pdf',
        file_base64: '',
      }];
    }
  }

  // PAYMENT METHOD ID FOR NET TERMS AND CREDIT CARD FOR FREIGHTCOM
  const payload: any = {
    order_id: orderId,
    unique_id: shipmentReference,
    service_id: selectedRate.serviceCode,
    payment_method_id: 'Wtdv8666ldARqUIc8jzyDMxVMvB8NYlx',
    customs_and_duties_payment_method_id: "FkqQn0CyrqG2JQHv1xhdYdXb5fD0YqA6",
    warehouse_id: warehouseId,
    details,
    pickup_details,
  };

  // Only include customs data for international shipments
  if (customs_invoice) {
    payload.customs_invoice = customs_invoice;
    payload.paperless_customs_documents = paperless_customs_documents;
  }

  console.log('Get Label payload:', JSON.stringify(payload, null, 2));
  const response = await api.post('/freightcom/freightcom?action=label', payload);

  if (response.data.status === false) {
    throw new Error('Freightcom API request failed', { cause: { response: { data: response.data } } });
  }

  const labelData = response.data.results[0];
  return {
    labelUrl: labelData.labelUrl,
    trackingNumber: labelData.trackingNumber,
    carrier: labelData.carrier,
  };
};

